import { useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { InlineTextarea } from '@/shared/components/custom/form-field/InlineTextarea';

interface InlineTaskCreatorProps {
  onConfirm: (title: string) => void;
  onCancel: () => void;
}

export function InlineTaskCreator({ onConfirm, onCancel }: InlineTaskCreatorProps) {
  const { t } = useTranslation('tasks');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    rootRef.current?.scrollIntoView({ block: 'nearest' });
  }, []);

  function handleConfirm() {
    const title = textareaRef.current?.value.trim() ?? '';
    if (!title) return;
    onConfirm(title);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') onCancel();
  }

  return (
    <div
      ref={rootRef}
      data-testid="tasks-inline-task-creator"
      className="rounded-md border border-primary/40 bg-card p-3 shadow-sm"
    >
      <InlineTextarea
        ref={textareaRef}
        rows={2}
        onKeyDown={handleKeyDown}
        placeholder={t('boardView.newTask.placeholder')}
        data-testid="tasks-inline-task-creator-input"
      />
      <div className="mt-2 flex justify-end gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={onCancel}
          data-testid="tasks-inline-task-creator-cancel"
        >
          <X className="size-3" />
          {t('boardView.newTask.cancel')}
        </Button>
        <Button
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={handleConfirm}
          data-testid="tasks-inline-task-creator-confirm"
        >
          <Check className="size-3" />
          {t('boardView.newTask.confirm')}
        </Button>
      </div>
    </div>
  );
}
